import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import { Flip } from "gsap/Flip";

import { breakpoints } from "@tokens/breakpoints";

import { stopScroller, startScroller } from "../utils/functions";

const expandProp = "borderRadius";
const navBreakMq = window.matchMedia(`(max-width: ${breakpoints["nav-break"] - 1}px)`);

let menuEl: HTMLDialogElement | null = null;
let menuTrigger: HTMLButtonElement | null = null;
let logoEl: HTMLElement | null = null;
let menuTriggerScrollTrigger: ScrollTrigger | null = null;
let suppressTriggerShow = false;
let expandEl: HTMLDivElement | null = null;
let lastOpenOrigin: HTMLElement | null = null;
let isAnimating = false;
let pendingClose: Promise<void> | null = null;

function hasLayout(el: Element): boolean {
    const rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
}

function ensureExpandEl(menu: HTMLDialogElement): HTMLDivElement {
    if (expandEl) return expandEl;

    expandEl = document.createElement("div");
    expandEl.className = "menu__expand";
    expandEl.setAttribute("aria-hidden", "true");
    gsap.set(expandEl, { autoAlpha: 0 });
    menu.parentElement?.insertBefore(expandEl, menu);

    return expandEl;
}

function createMenuTriggerScrollTrigger() {
    if (!menuTrigger) return;

    gsap.set(menuTrigger, { autoAlpha: 0 });

    menuTriggerScrollTrigger = ScrollTrigger.create({
        start: "100svh top",
        onEnter: () => gsap.to(menuTrigger!, { autoAlpha: 1, duration: 0.3 }),
        onLeaveBack: () => gsap.to(menuTrigger!, { autoAlpha: 0, duration: 0.3 }),
    });
}

export function resetMenuTrigger() {
    suppressTriggerShow = false;
    createMenuTriggerScrollTrigger();
}

export function hideMenuTrigger() {
    if (expandEl) {
        gsap.killTweensOf(expandEl);
        gsap.set(expandEl, { autoAlpha: 0 });
    }

    if (!menuTrigger) return;

    suppressTriggerShow = true;
    gsap.killTweensOf(menuTrigger);
    gsap.to(menuTrigger, { autoAlpha: 0, duration: 0.2 });
}

function setLogoVisible(visible: boolean) {
    if (!logoEl || !navBreakMq.matches) return;

    gsap.killTweensOf(logoEl);
    gsap.to(logoEl, { autoAlpha: visible ? 1 : 0, duration: 0.2 });
}

function setTriggerExpanded(expanded: boolean) {
    document.querySelectorAll<HTMLElement>('button[aria-controls="menu"]').forEach(trigger => {
        trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
    });
}

interface CloseMenuOptions {
    keepTriggerHidden?: boolean;
}

export function closeMenu(options: CloseMenuOptions = {}): Promise<void> {
    const menu = menuEl;
    if (!menu || !menu.open) return Promise.resolve();
    if (pendingClose) return pendingClose;

    if (isAnimating) {
        gsap.killTweensOf(menu);
        if (expandEl) gsap.killTweensOf(expandEl);
        if (menuTrigger) gsap.killTweensOf(menuTrigger);

        setTriggerExpanded(false);
        suppressTriggerShow = true;
        setLogoVisible(true);

        menu.close();
        startScroller();

        isAnimating = false;

        return Promise.resolve();
    }

    isAnimating = true;

    const showTrigger = !options.keepTriggerHidden && (menuTriggerScrollTrigger?.isActive ?? false);

    pendingClose = new Promise<void>(resolve => {
        const finish = () => {
            setTriggerExpanded(false);
            suppressTriggerShow = true;
            setLogoVisible(true);

            menu.close();
            startScroller();

            isAnimating = false;
            pendingClose = null;

            resolve();
        };

        gsap.killTweensOf(menu);
        if (menuTrigger) gsap.killTweensOf(menuTrigger);

        const originEl = lastOpenOrigin && hasLayout(lastOpenOrigin) ? lastOpenOrigin : menuTrigger;
        const panel = menu.querySelector<HTMLElement>(".menu__inner");

        if (!navBreakMq.matches && expandEl && originEl && hasLayout(originEl) && panel && hasLayout(panel)) {
            const expand = expandEl;
            gsap.killTweensOf(expand);

            Flip.fit(expand, panel, { duration: 0, props: expandProp });
            gsap.set(expand, { autoAlpha: 1 });

            const shrink = Flip.fit(expand, originEl, {
                duration: 0.4,
                ease: "power3.inOut",
                absolute: true,
                props: expandProp,
            }) as gsap.core.Tween;

            gsap.timeline({ onComplete: finish })
                .to(menu, { autoAlpha: 0, duration: 0.2 })
                .add(shrink, "-=0.05")
                .add(() => {
                    if (menuTrigger && showTrigger) gsap.set(menuTrigger, { autoAlpha: 1 });
                })
                .to(expand, { autoAlpha: 0, duration: 0.2 });
        } else {
            const tl = gsap.timeline({ onComplete: finish }).to(menu, { autoAlpha: 0, duration: 0.2 });
            if (menuTrigger && showTrigger) tl.to(menuTrigger, { autoAlpha: 1, duration: 0.2 }, "<");
        }
    });

    return pendingClose;
}

function openMenu(origin: HTMLElement) {
    const menu = menuEl;
    if (!menu || menu.open || isAnimating) return;

    isAnimating = true;
    lastOpenOrigin = origin;

    setLogoVisible(false);
    stopScroller();

    gsap.killTweensOf(menu);
    gsap.set(menu, { autoAlpha: 0 });

    if (menuTrigger) gsap.to(menuTrigger, { autoAlpha: 0, duration: 0.2 });

    menu.show();

    setTriggerExpanded(true);
    menu.focus();

    const panel = menu.querySelector<HTMLElement>(".menu__inner");

    if (!navBreakMq.matches && hasLayout(origin) && panel && hasLayout(panel)) {
        const expand = ensureExpandEl(menu);
        gsap.killTweensOf(expand);

        Flip.fit(expand, origin, { duration: 0, props: expandProp });
        gsap.set(expand, { autoAlpha: 1 });

        const grow = Flip.fit(expand, panel, {
            duration: 0.5,
            ease: "power3.inOut",
            absolute: true,
            props: expandProp,
        }) as gsap.core.Tween;

        gsap.timeline({
            onComplete: () => {
                isAnimating = false;
            },
        })
            .add(grow)
            .to(menu, { autoAlpha: 1, duration: 0.25 }, "-=0.15")
            .to(expand, { autoAlpha: 0, duration: 0.15 });
    } else {
        gsap.to(menu, {
            autoAlpha: 1,
            duration: 0.2,
            onComplete: () => {
                isAnimating = false;
            },
        });
    }
}

export default function initMenu() {
    const menu = document.querySelector<HTMLDialogElement>(".menu");
    if (!menu) return;

    menuEl = menu;
    logoEl = document.querySelector<HTMLElement>(".logo");

    menuTrigger = document.querySelector<HTMLButtonElement>(".menu__trigger");
    if (menuTrigger) {
        menuTrigger.hidden = false;
        createMenuTriggerScrollTrigger();
    }

    document.body.addEventListener("click", event => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const trigger = target.closest<HTMLElement>('button[aria-controls="menu"]');
        if (!trigger) return;

        event.preventDefault();
        openMenu(trigger);
    });

    menu.addEventListener("click", event => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        if (!target.closest(".menu__inner")) {
            void closeMenu();
            return;
        }

        if (target.closest("[data-close-menu]")) {
            void closeMenu();
            return;
        }

        if (target.closest("a[href]")) {
            void closeMenu({ keepTriggerHidden: true });
            return;
        }
    });

    menu.addEventListener("cancel", event => {
        event.preventDefault();
        void closeMenu();
    });

    document.addEventListener("keydown", event => {
        if (event.key !== "Escape") return;
        if (!menu.open) return;

        void closeMenu();
    });

    menu.addEventListener("close", () => {
        isAnimating = false;

        setTriggerExpanded(false);
        if (menuTrigger && !suppressTriggerShow) {
            gsap.to(menuTrigger, { autoAlpha: menuTriggerScrollTrigger?.isActive ? 1 : 0, duration: 0.2 });
        }

        suppressTriggerShow = false;

        startScroller();
    });
}
